import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  ReferenceLine,
} from 'recharts';
import { Copy, Edit, Trash2, Download, Maximize2 } from 'lucide-react';
import ResponsiveChart from '../../../shared/ui/ResponsiveChart';
import { formatTableCellValue } from '../../../shared/utils/formatters';
import VirtualizedTable from '../../../shared/ui/VirtualizedTable';
import { calculateStats, getTableColumnsCache, getWidgetSeriesCache } from '../../../shared/utils/seriesCache';

// Funciones helper simplificadas (mantén las tuyas originales)
const formatNumber = (num: number, decimals: number = 0) => {
  return num.toLocaleString('es-ES', { maximumFractionDigits: decimals });
};

const formatPercentage = (num: number, decimals: number = 1) => {
  return (num * 100).toFixed(decimals) + '%';
};

const safeText = (text: any) => {
  if (typeof text === 'string') return text;
  if (text === null || text === undefined) return '';
  return String(text);
};

const formatColumnLabel = (name: string) =>
  name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const generateWidgetTitle = ({ metric, dimension, aggregation }: any) => {
  return `${aggregation} de ${metric} por ${dimension}`;
};

const applyDataTransform = (
  data: any[],
  options: {
    sortBy?: string;
    topN?: number;
    minValue?: number;
    labelFilter?: string;
  },
  xField: string,
  yField: string
) => {
  let next = [...data];
  const { sortBy, topN, minValue, labelFilter } = options;

  if (labelFilter) {
    const filterText = labelFilter.toLowerCase();
    next = next.filter((item) =>
      safeText(item?.[xField]).toLowerCase().includes(filterText)
    );
  }

  if (typeof minValue === 'number' && !Number.isNaN(minValue)) {
    next = next.filter((item) => {
      const value = item?.[yField];
      return typeof value === 'number' ? value >= minValue : true;
    });
  }

  if (sortBy && sortBy !== 'none') {
    const compare = (a: any, b: any) => {
      switch (sortBy) {
        case 'value_desc':
          return (b?.[yField] ?? 0) - (a?.[yField] ?? 0);
        case 'value_asc':
          return (a?.[yField] ?? 0) - (b?.[yField] ?? 0);
        case 'label_desc':
          return safeText(b?.[xField]).localeCompare(safeText(a?.[xField]));
        case 'label_asc':
          return safeText(a?.[xField]).localeCompare(safeText(b?.[xField]));
        default:
          return 0;
      }
    };
    next = [...next].sort(compare);
  }

  if (typeof topN === 'number' && topN > 0) {
    next = next.slice(0, topN);
  }

  return next;
};

const getColorWithAlpha = (color: string, alpha: number) => {
  if (!color) return `rgba(14, 165, 233, ${alpha})`;
  const hex = color.replace('#', '');
  if (hex.length !== 6) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const resolveColorPalette = (colors?: string[]) => {
  if (!colors || colors.length === 0) {
    return COLORS;
  }
  return colors.map((color, index) => {
    if (typeof color === 'string' && color.trim().length > 0) {
      return color;
    }
    return COLORS[index % COLORS.length];
  });
};

const buildTrendlineData = (data: any[], yField: string) => {
  if (data.length < 2) return data;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  data.forEach((item, index) => {
    const x = index;
    const y = typeof item?.[yField] === 'number' ? item[yField] : 0;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  });

  const denominator = data.length * sumXX - sumX * sumX;
  const slope = denominator !== 0 ? (data.length * sumXY - sumX * sumY) / denominator : 0;
  const intercept = data.length ? (sumY - slope * sumX) / data.length : 0;

  return data.map((item, index) => ({
    ...item,
    trend: intercept + slope * index
  }));
};

// Custom Tooltip mejorado y más compacto
const CustomTooltip = ({ active, payload, label, data, yField }: any) => {
  if (!active || !payload || !payload.length) return null;

  const resolvedYField = yField || "valor";
  const stats = data && resolvedYField ? calculateStats(data, resolvedYField) : null;
  
  const currentIndex = data?.findIndex((item: any) => item[Object.keys(item)[0]] === label);
  const previousPoint = currentIndex > 0 ? data[currentIndex - 1] : null;
  const currentValue = payload[0]?.value;
  const previousValue = previousPoint?.[resolvedYField];
  
  let delta: number | null = null;
  let deltaPercent: number | null = null;
  if (previousValue !== undefined && typeof currentValue === 'number' && typeof previousValue === 'number') {
    delta = currentValue - previousValue;
    deltaPercent = previousValue !== 0 ? ((delta / previousValue) * 100) : 0;
  }

  let percentOfTotal: number | null = null;
  let ranking: number | null = null;
  let totalItems = 0;
  
  if (data && resolvedYField && typeof currentValue === 'number') {
    const total = data.reduce((sum: number, item: any) => {
      const val = typeof item[resolvedYField] === 'number' ? item[resolvedYField] : 0;
      return sum + val;
    }, 0);
    
    if (total > 0) {
      percentOfTotal = (currentValue / total) * 100;
    }
    
    const sortedValues = [...data]
      .sort((a, b) => (b[resolvedYField] || 0) - (a[resolvedYField] || 0))
      .map(item => item[resolvedYField]);
    
    ranking = sortedValues.indexOf(currentValue) + 1;
    totalItems = data.length;
  }

  return (
    <div 
      className="bg-slate-900/98 backdrop-blur-sm border border-sky-400/20 rounded-lg p-3 shadow-xl"
      style={{ 
        position: 'relative',
        zIndex: 99999,
        pointerEvents: 'none',
        minWidth: '180px',
        maxWidth: '240px'
      }}
    >
      {label && (
        <p className="text-white text-xs font-bold mb-2 pb-1.5 border-b border-sky-400/10">
          {label}
        </p>
      )}
      {payload.map((entry: any, index: number) => {
        const value = entry.value;
        const formattedValue = formatNumber(value, 0);
        
        return (
          <div key={index}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <div 
                  className="w-2 h-2 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: entry.color || entry.fill || '#0EA5E9' }}
                />
                <span className="text-slate-300 text-xs">Valor</span>
              </div>
                <span className="text-sky-400 text-sm font-bold tabular-nums">
                {formattedValue}
              </span>
            </div>
            
            {delta !== null && (
              <div className="mt-1.5 pt-1.5 border-t border-slate-700/30">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">vs Anterior</span>
                  <span className={`font-semibold ${delta >= 0 ? 'text-sky-400' : 'text-red-400'}`}>
                    {delta >= 0 ? '↑' : '↓'} {formatNumber(Math.abs(delta), 0)}
                  </span>
                </div>
              </div>
            )}
            
            {(percentOfTotal !== null || ranking !== null) && (
              <div className="mt-1.5 pt-1.5 border-t border-slate-700/30 space-y-1">
                {percentOfTotal !== null && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">% Total</span>
                    <span className="text-blue-400 font-semibold">{percentOfTotal.toFixed(1)}%</span>
                  </div>
                )}
                {ranking !== null && totalItems > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">Ranking</span>
                    <span className="text-amber-400 font-semibold">
                      #{ranking} de {totalItems}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const COLORS = ['#0EA5E9', '#38BDF8', '#7DD3FC', '#0284C7', '#0369A1'];

interface WidgetRendererProps {
  widget: any;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onExport?: () => void;
  onMaximize?: () => void;
  containerWidth?: number;
  containerHeight?: number;
  isConfigOpen?: boolean;
  onCloseConfig?: () => void;
  onSaveConfig?: (config: Record<string, any>) => Promise<void> | void;
}

interface WidgetCardProps {
  isHovered: boolean;
  onHoverChange: (nextHovered: boolean) => void;
  children: React.ReactNode;
}

const WidgetCard: React.FC<WidgetCardProps> = ({ isHovered, onHoverChange, children }) => (
  <div
    className="h-full bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl border border-slate-700/50 shadow-lg hover:shadow-2xl hover:border-sky-500/30 transition-all duration-300 overflow-hidden"
    onMouseEnter={() => onHoverChange(true)}
    onMouseLeave={() => onHoverChange(false)}
  >
    <div className="h-full p-4 flex flex-col">
      {children}
    </div>

    {/* Subtle gradient overlay on hover */}
    <div
      className={`absolute inset-0 bg-gradient-to-br from-sky-500/0 to-sky-500/5 pointer-events-none transition-opacity duration-300 ${
        isHovered ? 'opacity-100' : 'opacity-0'
      }`}
    />
  </div>
);

interface WidgetHeaderProps {
  widget: any;
  inferredTitle: string;
  totalRecords: number;
  isHovered: boolean;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onExport?: () => void;
  onMaximize?: () => void;
}

const WidgetHeader: React.FC<WidgetHeaderProps> = ({
  widget,
  inferredTitle,
  totalRecords,
  isHovered,
  onEdit,
  onDuplicate,
  onDelete,
  onExport,
  onMaximize,
}) => (
  <div className="relative">
    {/* Action Bar - Solo visible en hover */}
    <div
      className={`absolute -top-2 right-2 flex items-center gap-1 transition-all duration-200 ${
        isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1 pointer-events-none'
      }`}
    >
      {onDuplicate && (
        <button
          onClick={onDuplicate}
          className="p-1.5 bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white rounded-md transition-colors"
          title="Duplicar widget"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
      {onEdit && (
        <button
          onClick={onEdit}
          className="p-1.5 bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white rounded-md transition-colors"
          title="Editar widget"
        >
          <Edit className="w-3.5 h-3.5" />
        </button>
      )}
      {onMaximize && (
        <button
          onClick={onMaximize}
          className="p-1.5 bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white rounded-md transition-colors"
          title="Maximizar"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      )}
      {onExport && (
        <button
          onClick={onExport}
          className="p-1.5 bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white rounded-md transition-colors"
          title="Exportar datos"
        >
          <Download className="w-3.5 h-3.5" />
        </button>
      )}
      {onDelete && (
        <button
          onClick={onDelete}
          className="p-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 rounded-md transition-colors"
          title="Eliminar widget"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>

    {/* Header Content */}
    <div className="mb-3">
      {/* Badge de tipo de gráfico */}
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sky-500/10 text-sky-400 border border-sky-500/20">
          {widget.type === 'bar' && '📊 Barras'}
          {widget.type === 'line' && '📈 Línea'}
          {widget.type === 'pie' && '🥧 Torta'}
          {widget.type === 'metric' && '📌 Métrica'}
          {widget.type === 'table' && '📋 Tabla'}
        </span>
        {totalRecords > 0 && (
          <span className="text-xs text-slate-500">
            {totalRecords} registros
          </span>
        )}
      </div>

      {/* Título */}
      <h3 className="text-slate-100 text-sm font-semibold mb-1 leading-tight">
        {inferredTitle}
      </h3>

      {/* Micro-contexto */}
      {widget.dateRange && (
        <p className="text-sky-400/70 text-xs font-medium">
          📅 {widget.dateRange}
        </p>
      )}
    </div>
  </div>
);

const EmptyState = () => (
  <div className="h-full flex flex-col items-center justify-center text-center p-6">
    <div className="w-16 h-16 mb-4 rounded-xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
      <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    </div>
    <h3 className="text-slate-400 text-sm font-semibold mb-2">Sin datos disponibles</h3>
    <p className="text-slate-500 text-xs max-w-xs">
      Verifica los filtros o la configuración de este widget
    </p>
  </div>
);

const WidgetConfigPanel: React.FC<{
  isOpen: boolean;
  title: string;
  children: React.ReactNode;
  onClose?: () => void;
  onSave?: () => void;
  isDirty?: boolean;
  isSaving?: boolean;
  onFocusCapture?: React.FocusEventHandler<HTMLDivElement>;
  onBlurCapture?: React.FocusEventHandler<HTMLDivElement>;
}> = ({ isOpen, title, children, onClose, onSave, isDirty, isSaving, onFocusCapture, onBlurCapture }) => {
  if (!isOpen) return null;
  const panel = (
    <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col border-l border-slate-700 bg-slate-900 shadow-2xl">
      <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-100">Personalizar</p>
          <p className="truncate text-xs text-slate-400">{title}</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            title="Cerrar panel"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      <div
        className="flex-1 space-y-4 overflow-y-auto p-4 text-xs text-slate-300"
        onFocusCapture={onFocusCapture}
        onBlurCapture={onBlurCapture}
      >
        {children}
      </div>
      {onSave ? (
        <div className="border-t border-slate-700 px-4 py-3">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>{isDirty ? 'Cambios pendientes' : 'Sin cambios'}</span>
            <button
              type="button"
              onClick={onSave}
              disabled={!isDirty || isSaving}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                isDirty && !isSaving
                  ? 'bg-sky-600 text-white hover:bg-sky-500'
                  : 'bg-slate-800 text-slate-500 cursor-not-allowed'
              }`}
            >
              {isSaving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  if (typeof document === 'undefined') return panel;
  return createPortal(panel, document.body);
};

const isNumericValue = (value: unknown): boolean => {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.');
    return normalized.trim().length > 0 && Number.isFinite(Number(normalized));
  }
  return false;
};

const WidgetRendererInner: React.FC<WidgetRendererProps> = ({ 
  widget,
  onEdit,
  onDuplicate,
  onDelete,
  onExport,
  onMaximize,
  containerWidth,
  containerHeight,
  isConfigOpen = false,
  onCloseConfig,
  onSaveConfig,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [customConfig, setCustomConfig] = useState<Record<string, any>>(() => widget?.config || {});
  const [textDrafts, setTextDrafts] = useState<Record<string, string>>({});
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const isEditingConfigRef = React.useRef(false);
  const lastConfigSnapshotRef = React.useRef<string>('');

  // Guard clause
  if (!widget || typeof widget !== 'object') {
    return null;
  }
  
  const widgetIsValid = (
    typeof widget.id === 'string' &&
    typeof widget.title === 'string' &&
    typeof widget.type === 'string' &&
    Array.isArray(widget.data)
  );
  
  if (!widgetIsValid) {
    return null;
  }
  
  const data = widget.data || [];
  React.useEffect(() => {
    const nextConfig = widget.config || {};
    const snapshot = JSON.stringify(nextConfig);
    if (snapshot === lastConfigSnapshotRef.current) {
      return;
    }
    lastConfigSnapshotRef.current = snapshot;
    if (isEditingConfigRef.current) return;
    setCustomConfig(nextConfig);
    setTextDrafts({});
  }, [widget.config]);

  const persistedConfig = useMemo(() => widget.config || {}, [widget.config]);
  const effectiveConfig = useMemo(
    () => ({
      ...customConfig,
      ...textDrafts,
    }),
    [customConfig, textDrafts]
  );
  const isConfigDirty = useMemo(() => {
    return JSON.stringify(effectiveConfig) !== JSON.stringify(persistedConfig);
  }, [effectiveConfig, persistedConfig]);
  const { detectedXField, xField, yField, stats } = getWidgetSeriesCache({
    widgetId: widget.id,
    data,
    config: effectiveConfig,
    title: effectiveConfig.title || widget.title,
    defaultXField: 'name',
    defaultYField: 'value',
  });
  const standardYField = "valor";
  const resolveField = (
    field: string | null | undefined,
    fields: string[],
    fallback: string,
    options?: { preferNumeric?: boolean }
  ) => {
    if (field && fields.includes(field)) return field;
    if (options?.preferNumeric) {
      const numeric = fields.find((key) => isNumericValue(data?.[0]?.[key]));
      if (numeric) return numeric;
    }
    if (fallback && fields.includes(fallback)) return fallback;
    return fields[0] || fallback;
  };
  
  const inferredTitle = (safeText(effectiveConfig.title || widget.title) || generateWidgetTitle({
    metric: yField,
    dimension: xField,
    aggregation: effectiveConfig.aggregation || 'sum'
  }));
  const availableFields = useMemo(() => (data[0] ? Object.keys(data[0]) : []), [data]);
  const canSaveConfig = Boolean(onSaveConfig);

  const handleConfigChange = (updates: Record<string, any>) => {
    isEditingConfigRef.current = true;
    setCustomConfig((prev) => ({ ...prev, ...updates }));
  };

  const handleTextConfigChange = (key: string, value: string) => {
    isEditingConfigRef.current = true;
    setTextDrafts((prev) => ({ ...prev, [key]: value }));
    setCustomConfig((prev) => ({ ...prev, [key]: value }));
  };

  const getTextValue = (key: string, fallback = '') => {
    return textDrafts[key] ?? (effectiveConfig?.[key] ?? fallback);
  };

  const handleSaveConfig = async () => {
    if (!onSaveConfig || !isConfigDirty) return;
    setIsSavingConfig(true);
    try {
      await onSaveConfig(customConfig);
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleConfigFocusCapture = () => {
    isEditingConfigRef.current = true;
  };

  const handleConfigBlurCapture: React.FocusEventHandler<HTMLDivElement> = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      isEditingConfigRef.current = false;
    }
  };

  const handleHoverChange = React.useCallback((nextHovered: boolean) => {
    setIsHovered(nextHovered);
  }, []);

  const configPanelProps = {
    isOpen: isConfigOpen,
    onClose: onCloseConfig,
    onSave: canSaveConfig ? handleSaveConfig : undefined,
    isDirty: canSaveConfig ? isConfigDirty : false,
    isSaving: canSaveConfig ? isSavingConfig : false,
    onFocusCapture: handleConfigFocusCapture,
    onBlurCapture: handleConfigBlurCapture,
  };
  const totalRecords = data.length;

  switch (widget.type) {
    case 'bar':
      // Bar charts need at least 1 data point with dimension field
      if (data.length > 0 && (detectedXField || effectiveConfig.xField)) {
        const actualXField = resolveField(effectiveConfig.xField || detectedXField, availableFields, 'category');
        const actualYField = resolveField(effectiveConfig.yField || yField, availableFields, standardYField, { preferNumeric: true });
        const chartData = applyDataTransform(
          data,
          {
            sortBy: effectiveConfig.sortBy,
            topN: effectiveConfig.topN,
            minValue: effectiveConfig.minValue,
            labelFilter: effectiveConfig.labelFilter
          },
          actualXField,
          actualYField
        );
        const colorPalette = resolveColorPalette(effectiveConfig.colors);
        return (
          <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
            <WidgetHeader
              widget={widget}
              inferredTitle={inferredTitle}
              totalRecords={totalRecords}
              isHovered={isHovered}
              onDuplicate={onDuplicate}
              onEdit={onEdit}
              onMaximize={onMaximize}
              onExport={onExport}
              onDelete={onDelete}
            />
            <WidgetConfigPanel {...configPanelProps} title={inferredTitle}>
                <div className="grid grid-cols-1 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Título</span>
                    <input
                      type="text"
                      value={getTextValue('title')}
                      onChange={(event) => handleTextConfigChange('title', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="Título del gráfico"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Eje X</span>
                    <select
                      value={effectiveConfig.xField || actualXField}
                      onChange={(event) => handleConfigChange({ xField: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      {availableFields.map((field) => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Eje Y</span>
                    <select
                      value={effectiveConfig.yField || actualYField}
                      onChange={(event) => handleConfigChange({ yField: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      {availableFields.map((field) => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Label eje X</span>
                    <input
                      type="text"
                      value={getTextValue('xAxisLabel')}
                      onChange={(event) => handleTextConfigChange('xAxisLabel', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="Texto eje X"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Label eje Y</span>
                    <input
                      type="text"
                      value={getTextValue('yAxisLabel')}
                      onChange={(event) => handleTextConfigChange('yAxisLabel', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="Texto eje Y"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Orden</span>
                    <select
                      value={effectiveConfig.sortBy || 'none'}
                      onChange={(event) => handleConfigChange({ sortBy: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      <option value="none">Sin orden</option>
                      <option value="value_desc">Valor desc</option>
                      <option value="value_asc">Valor asc</option>
                      <option value="label_asc">Label A-Z</option>
                      <option value="label_desc">Label Z-A</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Top N</span>
                    <input
                      type="number"
                      min={1}
                      value={effectiveConfig.topN ?? ''}
                      onChange={(event) => handleConfigChange({ topN: event.target.value ? Number(event.target.value) : undefined })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Mínimo</span>
                    <input
                      type="number"
                      min={0}
                      value={effectiveConfig.minValue ?? ''}
                      onChange={(event) => handleConfigChange({ minValue: event.target.value ? Number(event.target.value) : undefined })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    />
                  </label>
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-slate-400">Filtro (contiene)</span>
                  <input
                    type="text"
                    value={getTextValue('labelFilter')}
                    onChange={(event) => handleTextConfigChange('labelFilter', event.target.value)}
                    className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    placeholder="Ej: NPK"
                  />
                </label>
                <div className="space-y-2">
                  <span className="text-slate-400">Colores por barra</span>
                  <div className="grid grid-cols-2 gap-2">
                    {chartData.map((entry: any, index: number) => (
                      <label key={`${entry?.[actualXField]}-${index}`} className="flex items-center gap-2">
                        <input
                          type="color"
                          value={colorPalette[index % colorPalette.length]}
                          onChange={(event) => {
                            const nextColors = [...(effectiveConfig.colors || colorPalette)];
                            nextColors[index] = event.target.value;
                            handleConfigChange({ colors: nextColors });
                          }}
                        />
                        <span className="text-slate-400">{safeText(entry?.[actualXField])}</span>
                      </label>
                    ))}
                  </div>
                </div>
            </WidgetConfigPanel>
            <div className="flex-1 min-h-0">
              <ResponsiveChart minHeight={200}>
                {({ width, height }) => (
                  <BarChart 
                    width={width}
                    height={height}
                    data={chartData} 
                    margin={{ top: 10, right: 20, left: 10, bottom: 60 }}
                    onMouseMove={(state: any) => {
                      if (state?.activeTooltipIndex !== undefined) {
                        setHoveredIndex(state.activeTooltipIndex);
                      }
                    }}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" opacity={0.3} />
                    <XAxis 
                      dataKey={actualXField} 
                      stroke="#64748b" 
                      fontSize={10}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                      label={effectiveConfig.xAxisLabel ? {
                        value: effectiveConfig.xAxisLabel,
                        position: 'insideBottom',
                        offset: -45,
                        fill: '#94a3b8',
                        fontSize: 10
                      } : undefined}
                    />
                    <YAxis 
                      stroke="#64748b" 
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => formatNumber(value, 0)}
                      label={effectiveConfig.yAxisLabel ? {
                        value: effectiveConfig.yAxisLabel,
                        angle: -90,
                        position: 'insideLeft',
                        fill: '#94a3b8',
                        fontSize: 10
                      } : undefined}
                    />
                    {stats && (
                      <ReferenceLine 
                        y={stats.avg} 
                        stroke="#f59e0b" 
                        strokeDasharray="3 3"
                        strokeWidth={1.5}
                        label={{ 
                          value: `Prom: ${formatNumber(stats.avg, 0)}`, 
                          position: 'right',
                          fill: '#f59e0b',
                          fontSize: 10,
                          fontWeight: 600
                        }}
                      />
                    )}
                    <Tooltip 
                      content={<CustomTooltip data={chartData} yField={actualYField} />}
                      cursor={{ fill: 'rgba(14, 165, 233, 0.1)' }}
                    />
                    <Bar 
                      dataKey={actualYField} 
                      radius={[6, 6, 0, 0]}
                      isAnimationActive={false}
                    >
                      {chartData.map((entry: any, index: number) => {
                        const baseColor = colorPalette[index % colorPalette.length];
                        return (
                        <Cell 
                          key={`cell-${index}`}
                          fill={hoveredIndex === null || hoveredIndex === index ? baseColor : getColorWithAlpha(baseColor, 0.35)}
                        />
                      );})}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveChart>
            </div>
          </WidgetCard>
        );
      }
      return (
        <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
          <EmptyState />
        </WidgetCard>
      );
      
    case 'line':
      // Line charts for temporal series
      if (data.length > 0 && (detectedXField || effectiveConfig.xField || xField)) {
        const actualXField = resolveField(effectiveConfig.xField || xField, availableFields, 'date');
        const actualYField = resolveField(effectiveConfig.yField || yField, availableFields, standardYField, { preferNumeric: true });
        const chartData = applyDataTransform(
          data,
          {
            sortBy: effectiveConfig.sortBy,
            topN: effectiveConfig.topN,
            minValue: effectiveConfig.minValue,
            labelFilter: effectiveConfig.labelFilter
          },
          actualXField,
          actualYField
        );
        const lineData = effectiveConfig.showTrendline ? buildTrendlineData(chartData, actualYField) : chartData;
        const lineColor = effectiveConfig.lineColor || '#0EA5E9';
        const trendlineColor = effectiveConfig.trendlineColor || '#f59e0b';
        return (
          <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
            <WidgetHeader
              widget={widget}
              inferredTitle={inferredTitle}
              totalRecords={totalRecords}
              isHovered={isHovered}
              onDuplicate={onDuplicate}
              onEdit={onEdit}
              onMaximize={onMaximize}
              onExport={onExport}
              onDelete={onDelete}
            />
            <WidgetConfigPanel {...configPanelProps} title={inferredTitle}>
                <div className="grid grid-cols-1 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Título</span>
                    <input
                      type="text"
                      value={getTextValue('title')}
                      onChange={(event) => handleTextConfigChange('title', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="Título del gráfico"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Eje X</span>
                    <select
                      value={effectiveConfig.xField || actualXField}
                      onChange={(event) => handleConfigChange({ xField: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      {availableFields.map((field) => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Eje Y</span>
                    <select
                      value={effectiveConfig.yField || actualYField}
                      onChange={(event) => handleConfigChange({ yField: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      {availableFields.map((field) => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Label eje X</span>
                    <input
                      type="text"
                      value={getTextValue('xAxisLabel')}
                      onChange={(event) => handleTextConfigChange('xAxisLabel', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="Texto eje X"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Label eje Y</span>
                    <input
                      type="text"
                      value={getTextValue('yAxisLabel')}
                      onChange={(event) => handleTextConfigChange('yAxisLabel', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="Texto eje Y"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Orden</span>
                    <select
                      value={effectiveConfig.sortBy || 'none'}
                      onChange={(event) => handleConfigChange({ sortBy: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      <option value="none">Sin orden</option>
                      <option value="value_desc">Valor desc</option>
                      <option value="value_asc">Valor asc</option>
                      <option value="label_asc">Label A-Z</option>
                      <option value="label_desc">Label Z-A</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Top N</span>
                    <input
                      type="number"
                      min={1}
                      value={effectiveConfig.topN ?? ''}
                      onChange={(event) => handleConfigChange({ topN: event.target.value ? Number(event.target.value) : undefined })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Mínimo</span>
                    <input
                      type="number"
                      min={0}
                      value={effectiveConfig.minValue ?? ''}
                      onChange={(event) => handleConfigChange({ minValue: event.target.value ? Number(event.target.value) : undefined })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    />
                  </label>
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-slate-400">Filtro (contiene)</span>
                  <input
                    type="text"
                    value={getTextValue('labelFilter')}
                    onChange={(event) => handleTextConfigChange('labelFilter', event.target.value)}
                    className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    placeholder="Ej: 2026-01"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="color"
                      value={lineColor}
                      onChange={(event) => handleConfigChange({ lineColor: event.target.value })}
                    />
                    <span className="text-slate-400">Color línea</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="color"
                      value={trendlineColor}
                      onChange={(event) => handleConfigChange({ trendlineColor: event.target.value })}
                    />
                    <span className="text-slate-400">Color tendencia</span>
                  </label>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(effectiveConfig.showTrendline)}
                    onChange={(event) => handleConfigChange({ showTrendline: event.target.checked })}
                  />
                  <span className="text-slate-400">Mostrar línea de tendencia</span>
                </label>
            </WidgetConfigPanel>
            <div className="flex-1 min-h-0">
              <ResponsiveChart minHeight={200}>
                {({ width, height }) => (
                  <LineChart 
                    width={width}
                    height={height}
                    data={lineData} 
                    margin={{ top: 10, right: 20, left: 10, bottom: 60 }}
                    onMouseMove={(state: any) => {
                      if (state?.activeTooltipIndex !== undefined) {
                        setHoveredIndex(state.activeTooltipIndex);
                      }
                    }}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" opacity={0.3} />
                    <XAxis 
                      dataKey={actualXField} 
                      stroke="#64748b" 
                      fontSize={10}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                      label={effectiveConfig.xAxisLabel ? {
                        value: effectiveConfig.xAxisLabel,
                        position: 'insideBottom',
                        offset: -45,
                        fill: '#94a3b8',
                        fontSize: 10
                      } : undefined}
                    />
                    <YAxis 
                      stroke="#64748b" 
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => formatNumber(value, 0)}
                      label={effectiveConfig.yAxisLabel ? {
                        value: effectiveConfig.yAxisLabel,
                        angle: -90,
                        position: 'insideLeft',
                        fill: '#94a3b8',
                        fontSize: 10
                      } : undefined}
                    />
                    {stats && (
                      <ReferenceLine 
                        y={stats.avg} 
                        stroke="#f59e0b" 
                        strokeDasharray="3 3"
                        strokeWidth={1.5}
                        label={{ 
                          value: `Prom: ${formatNumber(stats.avg, 0)}`, 
                          position: 'right',
                          fill: '#f59e0b',
                          fontSize: 10,
                          fontWeight: 600
                        }}
                      />
                    )}
                    <Tooltip 
                      content={<CustomTooltip data={lineData} yField={actualYField} />} 
                      cursor={{ stroke: '#0EA5E9', strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey={actualYField} 
                      stroke={lineColor} 
                      strokeWidth={2.5} 
                      dot={(props: any) => {
                        const { cx, cy, index } = props;
                        const isHovered = hoveredIndex === null || hoveredIndex === index;
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={4}
                            fill={isHovered ? lineColor : getColorWithAlpha(lineColor, 0.35)}
                            stroke="#fff"
                            strokeWidth={1.5}
                          />
                        );
                      }}
                      activeDot={{ r: 6, fill: lineColor, stroke: '#fff', strokeWidth: 2 }}
                      isAnimationActive={false}
                    />
                    {effectiveConfig.showTrendline && (
                      <Line
                        type="monotone"
                        dataKey="trend"
                        stroke={trendlineColor}
                        strokeWidth={2}
                        dot={false}
                        strokeDasharray="6 3"
                        isAnimationActive={false}
                      />
                    )}
                  </LineChart>
                )}
              </ResponsiveChart>
            </div>
          </WidgetCard>
        );
      }
      return (
        <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
          <EmptyState />
        </WidgetCard>
      );
      
    case 'pie':
      // Pie charts for category distribution
      if (data.length > 0 && (detectedXField || effectiveConfig.xField)) {
        const actualXField = resolveField(effectiveConfig.xField || detectedXField, availableFields, 'category');
        const actualYField = resolveField(effectiveConfig.yField || yField, availableFields, standardYField, { preferNumeric: true });
        const chartData = applyDataTransform(
          data,
          {
            sortBy: effectiveConfig.sortBy,
            topN: effectiveConfig.topN,
            minValue: effectiveConfig.minValue,
            labelFilter: effectiveConfig.labelFilter
          },
          actualXField,
          actualYField
        );
        const colorPalette = resolveColorPalette(effectiveConfig.colors);
        return (
          <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
            <WidgetHeader
              widget={widget}
              inferredTitle={inferredTitle}
              totalRecords={totalRecords}
              isHovered={isHovered}
              onDuplicate={onDuplicate}
              onEdit={onEdit}
              onMaximize={onMaximize}
              onExport={onExport}
              onDelete={onDelete}
            />
            <WidgetConfigPanel {...configPanelProps} title={inferredTitle}>
                <div className="grid grid-cols-1 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Título</span>
                    <input
                      type="text"
                      value={getTextValue('title')}
                      onChange={(event) => handleTextConfigChange('title', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="Título del gráfico"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Dimensión</span>
                    <select
                      value={effectiveConfig.xField || actualXField}
                      onChange={(event) => handleConfigChange({ xField: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      {availableFields.map((field) => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Valor</span>
                    <select
                      value={effectiveConfig.yField || actualYField}
                      onChange={(event) => handleConfigChange({ yField: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      {availableFields.map((field) => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Orden</span>
                    <select
                      value={effectiveConfig.sortBy || 'none'}
                      onChange={(event) => handleConfigChange({ sortBy: event.target.value })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    >
                      <option value="none">Sin orden</option>
                      <option value="value_desc">Valor desc</option>
                      <option value="value_asc">Valor asc</option>
                      <option value="label_asc">Label A-Z</option>
                      <option value="label_desc">Label Z-A</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Top N</span>
                    <input
                      type="number"
                      min={1}
                      value={effectiveConfig.topN ?? ''}
                      onChange={(event) => handleConfigChange({ topN: event.target.value ? Number(event.target.value) : undefined })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Mínimo</span>
                    <input
                      type="number"
                      min={0}
                      value={effectiveConfig.minValue ?? ''}
                      onChange={(event) => handleConfigChange({ minValue: event.target.value ? Number(event.target.value) : undefined })}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    />
                  </label>
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-slate-400">Filtro (contiene)</span>
                  <input
                    type="text"
                    value={getTextValue('labelFilter')}
                    onChange={(event) => handleTextConfigChange('labelFilter', event.target.value)}
                    className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    placeholder="Ej: Directa"
                  />
                </label>
                <div className="space-y-2">
                  <span className="text-slate-400">Colores por porción</span>
                  <div className="grid grid-cols-2 gap-2">
                    {chartData.map((entry: any, index: number) => (
                      <label key={`${entry?.[actualXField]}-${index}`} className="flex items-center gap-2">
                        <input
                          type="color"
                          value={colorPalette[index % colorPalette.length]}
                          onChange={(event) => {
                            const nextColors = [...(effectiveConfig.colors || colorPalette)];
                            nextColors[index] = event.target.value;
                            handleConfigChange({ colors: nextColors });
                          }}
                        />
                        <span className="text-slate-400">{safeText(entry?.[actualXField])}</span>
                      </label>
                    ))}
                  </div>
                </div>
            </WidgetConfigPanel>
            <div className="flex-1 min-h-0 flex items-center justify-center">
              <ResponsiveChart minHeight={250}>
                {({ width, height }) => (
                  <PieChart width={width} height={height}>
                    <Pie
                      data={chartData}
                      dataKey={actualYField}
                      nameKey={actualXField}
                      cx="50%" 
                      cy="50%" 
                      innerRadius={50} 
                      outerRadius={75} 
                      paddingAngle={2}
                      isAnimationActive={false}
                    >
                      {chartData.map((entry: any, index: number) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={colorPalette[index % colorPalette.length]}
                          stroke="#1e293b"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={<CustomTooltip data={chartData} yField={actualYField} />} 
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={30}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '10px', color: '#94a3b8' }}
                    />
                  </PieChart>
                )}
              </ResponsiveChart>
            </div>
          </WidgetCard>
        );
      }
      return (
        <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
          <EmptyState />
        </WidgetCard>
      );
      
    case 'metric':
      // Metric widgets receive: [{valor: 123}]
      if (data.length > 0 && data[0]) {
        const metricKey = resolveField(effectiveConfig.yField || yField, availableFields, standardYField, { preferNumeric: true });
        if (typeof data[0][metricKey] === 'undefined') {
          return (
            <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
              <EmptyState />
            </WidgetCard>
          );
        }
        const currentValue = data[0][metricKey];
        const previousValue = data[1]?.[metricKey];
        const valuePrefix = effectiveConfig.valuePrefix || '';
        const valueSuffix = effectiveConfig.valueSuffix || '';
        
        let delta = null;
        let deltaPercent = null;
        
        if (previousValue !== undefined && typeof currentValue === 'number' && typeof previousValue === 'number') {
          delta = currentValue - previousValue;
          deltaPercent = previousValue !== 0 ? ((delta / previousValue) * 100) : 0;
        }
        
        const isPositive = delta === null || delta >= 0;
        
        return (
          <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
            <WidgetHeader
              widget={widget}
              inferredTitle={inferredTitle}
              totalRecords={totalRecords}
              isHovered={isHovered}
              onDuplicate={onDuplicate}
              onEdit={onEdit}
              onMaximize={onMaximize}
              onExport={onExport}
              onDelete={onDelete}
            />
            <WidgetConfigPanel {...configPanelProps} title={inferredTitle}>
                <label className="flex flex-col gap-1">
                  <span className="text-slate-400">Título</span>
                  <input
                    type="text"
                    value={getTextValue('title')}
                    onChange={(event) => handleTextConfigChange('title', event.target.value)}
                    className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    placeholder="Título del KPI"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Prefijo</span>
                    <input
                      type="text"
                      value={getTextValue('valuePrefix')}
                      onChange={(event) => handleTextConfigChange('valuePrefix', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="$"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-slate-400">Sufijo</span>
                    <input
                      type="text"
                      value={getTextValue('valueSuffix')}
                      onChange={(event) => handleTextConfigChange('valueSuffix', event.target.value)}
                      className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                      placeholder="tn"
                    />
                  </label>
                </div>
            </WidgetConfigPanel>
            <div className="flex-1 flex flex-col justify-center items-center text-center">
              <div className="relative">
                <div className="text-5xl font-bold bg-gradient-to-r from-sky-400 to-sky-500 bg-clip-text text-transparent mb-3">
                  {`${valuePrefix}${formatNumber(currentValue, 0)}${valueSuffix}`}
                </div>
                
                {delta !== null && (
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                      isPositive 
                        ? 'bg-sky-500/10 text-sky-400 border border-sky-500/20' 
                        : 'bg-red-500/10 text-red-400 border border-red-500/20'
                    }`}
                  >
                    <span className="text-xl">
                      {isPositive ? '↑' : '↓'}
                    </span>
                    <div className="text-left">
                      <div className="text-base font-bold">
                        {formatNumber(Math.abs(delta), 0)}
                      </div>
                      {deltaPercent !== null && (
                        <div className="text-xs opacity-70">
                          {formatPercentage(Math.abs(deltaPercent) / 100, 1)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </WidgetCard>
        );
      }
      return (
        <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
          <EmptyState />
        </WidgetCard>
      );

    case 'table':
      if (data.length > 0 && typeof data[0] === 'object') {
        const columns = getTableColumnsCache(widget.id, data);
        const defaultVisibleColumns = columns.slice(0, 8);
        const configuredVisibleColumns = Array.isArray(effectiveConfig.visibleColumns) ? effectiveConfig.visibleColumns : null;
        const selectedColumns = (configuredVisibleColumns && configuredVisibleColumns.length > 0
          ? configuredVisibleColumns
          : defaultVisibleColumns
        ).filter((column) => columns.includes(column));
        const visibleColumns = selectedColumns.length > 0 ? selectedColumns : defaultVisibleColumns;
        const tableFilter = safeText(effectiveConfig.tableFilter || '').trim().toLowerCase();
        const tableData = tableFilter
          ? data.filter((row: any) =>
              Object.values(row).some((value) =>
                safeText(value).toLowerCase().includes(tableFilter)
              )
            )
          : data;
        const tableMaxHeight = Math.max(200, (containerHeight ?? 360) - 140);
        const toggleColumnVisibility = (column: string) => {
          const next = new Set(selectedColumns);
          if (next.has(column)) {
            next.delete(column);
          } else {
            next.add(column);
          }
          handleConfigChange({ visibleColumns: columns.filter((col) => next.has(col)) });
        };
        const showAllColumns = () => {
          handleConfigChange({ visibleColumns: columns });
        };
        const resetColumns = () => {
          handleConfigChange({ visibleColumns: defaultVisibleColumns });
        };

        return (
          <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
            <WidgetHeader
              widget={widget}
              inferredTitle={inferredTitle}
              totalRecords={totalRecords}
              isHovered={isHovered}
              onDuplicate={onDuplicate}
              onEdit={onEdit}
              onMaximize={onMaximize}
              onExport={onExport}
              onDelete={onDelete}
            />
            <WidgetConfigPanel {...configPanelProps} title={inferredTitle}>
                <label className="flex flex-col gap-1">
                  <span className="text-slate-400">Título</span>
                  <input
                    type="text"
                    value={getTextValue('title')}
                    onChange={(event) => handleTextConfigChange('title', event.target.value)}
                    className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    placeholder="Título de la tabla"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-slate-400">Filtro rápido</span>
                  <input
                    type="text"
                    value={getTextValue('tableFilter')}
                    onChange={(event) => handleTextConfigChange('tableFilter', event.target.value)}
                    className="rounded bg-slate-950/60 border border-slate-700 px-2 py-1"
                    placeholder="Buscar por cualquier campo"
                  />
                </label>
                <div className="space-y-2 rounded-lg border border-slate-700/60 bg-slate-900/40 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Columnas visibles</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={showAllColumns}
                        className="text-xs text-sky-400 hover:text-sky-300"
                      >
                        Todas
                      </button>
                      <button
                        type="button"
                        onClick={resetColumns}
                        className="text-xs text-slate-400 hover:text-slate-200"
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                  <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
                    {columns.map((column) => (
                      <label key={column} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedColumns.includes(column)}
                          onChange={() => toggleColumnVisibility(column)}
                          className="h-3 w-3 rounded border-slate-600 bg-slate-950/60 text-sky-500 focus:ring-sky-500/30"
                        />
                        <span className="text-xs text-slate-300">{formatColumnLabel(column)}</span>
                      </label>
                    ))}
                  </div>
                </div>
            </WidgetConfigPanel>
            {visibleColumns.length === 0 ? (
              <div className="rounded-lg border border-slate-700/40 bg-slate-900/40 p-6 text-center text-sm text-slate-400">
                Selecciona al menos una columna para mostrar datos.
              </div>
            ) : (
              <VirtualizedTable
                columns={visibleColumns}
                data={tableData}
                formatCellValue={formatTableCellValue}
                formatHeader={formatColumnLabel}
                maxCellLength={60}
                rowHeight={32}
                overscan={8}
                maxHeight={tableMaxHeight}
                containerClassName="overflow-auto rounded-lg border border-slate-700/40"
                tableClassName="min-w-max w-full text-xs"
                theadClassName="bg-slate-800/60 sticky top-0"
                headerCellClassName="px-3 py-2 text-left text-slate-300 font-semibold whitespace-nowrap"
                rowClassName="border-b border-slate-800/60 hover:bg-slate-800/30"
                cellClassName="px-3 py-2 text-slate-400 whitespace-nowrap"
              />
            )}
          </WidgetCard>
        );
      }

      return (
        <WidgetCard isHovered={isHovered} onHoverChange={handleHoverChange}>
          <EmptyState />
        </WidgetCard>
      );
      
    default:
      return null;
  }
};

const WidgetRenderer = React.memo(WidgetRendererInner);

// Export the actual component as default
export default WidgetRenderer;

// Demo component (no longer default export)
export function WidgetRendererDemo() {
  const sampleWidgets = [
    {
      id: '1',
      title: 'Ventas Mensuales por Región',
      type: 'bar',
      dateRange: 'Últimos 30 días',
      data: [
        { region: 'Norte', valor: 45000 },
        { region: 'Sur', valor: 62000 },
        { region: 'Este', valor: 38000 },
        { region: 'Oeste', valor: 54000 },
      ]
    },
    {
      id: '2',
      title: 'Evolución de Ingresos',
      type: 'line',
      dateRange: 'Último trimestre',
      data: [
        { mes: 'Ene', valor: 120000 },
        { mes: 'Feb', valor: 145000 },
        { mes: 'Mar', valor: 132000 },
        { mes: 'Abr', valor: 168000 },
      ]
    },
    {
      id: '3',
      title: 'Distribución por Categoría',
      type: 'pie',
      dateRange: 'Año 2024',
      data: [
        { categoria: 'Producto A', valor: 35000 },
        { categoria: 'Producto B', valor: 28000 },
        { categoria: 'Producto C', valor: 42000 },
        { categoria: 'Producto D', valor: 19000 },
      ]
    },
    {
      id: '4',
      title: 'Ingresos Totales',
      type: 'metric',
      dateRange: 'Diciembre 2024',
      data: [
        { valor: 881467 },
        { valor: 820000 }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Professional Dashboard Widgets</h1>
          <p className="text-slate-400">Hover para ver controles • Click en widgets para interactuar</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sampleWidgets.map((widget) => (
            <div key={widget.id} className="h-[400px]">
              <WidgetRenderer
                widget={widget}
                onEdit={() => console.log('Edit', widget.id)}
                onDuplicate={() => console.log('Duplicate', widget.id)}
                onDelete={() => console.log('Delete', widget.id)}
                onExport={() => console.log('Export', widget.id)}
                onMaximize={() => console.log('Maximize', widget.id)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
